import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { loginRequest } from "../authConfig";

/**
 * Hook that checks whether the currently signed-in user belongs to the
 * AD group managed on the Access Management page.
 *
 * It calls GET /api/v1/admin/group-members on the backend, then checks
 * whether the user's email (or UPN) appears in the returned list.
 *
 * Returns { isMember, loading, error }
 */
const useGroupMembership = () => {
  const { instance, accounts } = useMsal();
  const baseUrl = useSelector((state) => state.url);

  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkMembership = useCallback(async () => {
    if (!accounts || accounts.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get the idToken to authenticate with our backend
      const resp = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      const res = await fetch(`${baseUrl}/api/v1/admin/group-members`, {
        headers: {
          Authorization: `Bearer ${resp.idToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || `HTTP ${res.status}`);
      }

      // Current user's email / UPN (case-insensitive match)
      const currentEmail = (
        accounts[0].username ||
        accounts[0].idTokenClaims?.preferred_username ||
        ""
      ).toLowerCase();

      const found = data.members.some(
        (m) =>
          (m.mail || "").toLowerCase() === currentEmail ||
          (m.userPrincipalName || "").toLowerCase() === currentEmail,
      );

      setIsMember(found);
    } catch (err) {
      console.error("[useGroupMembership] error:", err);
      setError(err.message);
      setIsMember(false);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, baseUrl]);

  useEffect(() => {
    checkMembership();
  }, [checkMembership]);

  return { isMember, loading, error };
};

export default useGroupMembership;
